// HUD layout runtime: applies the saved layout to the built-in DOM elements and
// renders custom value elements (bar / ring / pips / sparkline) from the same
// document. Layout composes with each element's own CSS through the individual
// `translate` / `scale` properties, so the shipped HUD is a no-op until moved.

import {
  HUD_ELEMENTS,
  clamp,
  type HudCustomSpec,
  type HudDocument,
  type HudElementLayout,
  type HudSource,
} from "./types";
import { evaluatePaint } from "./values";
import { hudStage, hudStageSize } from "../stage";

interface Entry {
  id: string;
  el: HTMLElement;
  layout: HudElementLayout | undefined;
  source: HudSource | null;
  custom: HudCustomSpec | null;
}

type HPart = "left" | "center" | "right" | null;
type VPart = "top" | "middle" | "bottom" | null;

function parts(anchor: HudDocument["elements"][number]["anchor"]): { h: HPart; v: VPart } {
  if (anchor === "auto") return { h: null, v: null };
  return {
    h: anchor.includes("left") ? "left" : anchor.includes("right") ? "right" : "center",
    v: anchor.includes("top") ? "top" : anchor.includes("bottom") ? "bottom" : "middle",
  };
}

function pivotX(rect: DOMRect, h: HPart): number {
  return h === "left" ? rect.left : h === "right" ? rect.right : rect.left + rect.width / 2;
}

function pivotY(rect: DOMRect, v: VPart): number {
  return v === "top" ? rect.top : v === "bottom" ? rect.bottom : rect.top + rect.height / 2;
}

function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class HudLayoutRuntime {
  private doc: HudDocument;
  private values: Record<HudSource, number> = {
    health: 100,
    armor: 0,
    ammo: 0,
    stack: 100,
    speed: 0,
  };
  private dirty = true;
  private raceMode = false;
  private editing = false;
  private gameShown = new Map<string, boolean>();
  private lastW = 0;
  private lastH = 0;
  private lastDpr = 0;
  private lastReconcile = 0;
  private customNodes = new Map<string, HTMLElement>();
  private customSig = new Map<string, string>();
  private spark = new Map<string, { values: number[]; last: number }>();

  constructor(doc: HudDocument) {
    this.doc = doc;
  }

  setDoc(doc: HudDocument): void {
    this.doc = doc;
    this.apply();
  }

  /** Race-like modes show elements flagged `raceOnly`. */
  setRaceMode(on: boolean): void {
    if (on === this.raceMode) return;
    this.raceMode = on;
    this.dirty = true;
  }

  /**
   * Semantic visibility from the game (in a match, spectating, flag present…).
   * Combined with the layout's `visible`/`raceOnly`, this is the single source
   * for whether an element is drawn. Group containers (`#combat-hud`, …) use
   * the same call even though they are not layout entries themselves.
   */
  setShown(id: string, on: boolean): void {
    if ((this.gameShown.get(id) ?? true) === on) return;
    this.gameShown.set(id, on);
    // Apply now: the editor can be opened from the menu with no game loop
    // running, and visibility must not wait for the next `tick`.
    this.apply();
  }

  /** The HUD editor shows every element, including ones the game would hide. */
  setEditing(on: boolean): void {
    if (on === this.editing) return;
    this.editing = on;
    this.apply();
  }

  /** Effective visibility: editor override + game state + layout flags. */
  isVisible(id: string): boolean {
    const layout = this.doc.elements.find((e) => e.id === id);
    // Removed built-ins are listed in the editor's "Removed" group, not drawn.
    if (layout?.removed) return false;
    if (this.editing) return true;
    if (this.gameShown.get(id) === false) return false;
    if (!layout) return true;
    return layout.visible !== false && (!layout.raceOnly || this.raceMode);
  }

  setValue(source: HudSource, value: number): void {
    if (this.values[source] === value) return;
    this.values[source] = value;
    this.applyValues(performance.now());
  }

  /** Call every frame; re-applies on resize and periodically (text changes). */
  tick(now: number): void {
    const dpr = window.devicePixelRatio || 1;
    const stage = hudStageSize();
    if (
      this.dirty ||
      stage.w !== this.lastW ||
      stage.h !== this.lastH ||
      dpr !== this.lastDpr ||
      now - this.lastReconcile > 750
    ) {
      this.lastReconcile = now;
      this.apply();
    }
  }

  rect(id: string): DOMRect | null {
    const e = document.getElementById(id);
    if (!e) return null;
    const r = e.getBoundingClientRect();
    return r.width || r.height ? r : null;
  }

  private valueFor(source: HudSource): number {
    if (source === "stack") return this.values.health + this.values.armor;
    return this.values[source];
  }

  private entries(): Entry[] {
    const byId = new Map(this.doc.elements.map((e) => [e.id, e]));
    const out: Entry[] = [];
    for (const def of HUD_ELEMENTS) {
      const e = document.getElementById(def.id);
      if (!e) continue;
      out.push({
        id: def.id,
        el: e,
        layout: byId.get(def.id),
        source: def.source ?? null,
        custom: null,
      });
    }
    for (const layout of this.doc.elements) {
      if (!layout.custom) continue;
      const e = this.ensureCustomNode(layout);
      out.push({ id: layout.id, el: e, layout, source: layout.custom.source, custom: layout.custom });
    }
    return out;
  }

  private syncCustomNodes(): void {
    const wanted = new Set(this.doc.elements.filter((e) => e.custom).map((e) => e.id));
    for (const [id, node] of this.customNodes) {
      if (wanted.has(id)) continue;
      node.remove();
      this.customNodes.delete(id);
      this.customSig.delete(id);
      this.spark.delete(id);
    }
  }

  private ensureCustomNode(layout: HudElementLayout): HTMLElement {
    let node = this.customNodes.get(layout.id);
    if (!node) {
      node = el("div", "hud-custom");
      node.id = layout.id;
      (hudStage() ?? document.body).append(node);
      this.customNodes.set(layout.id, node);
      this.customSig.delete(layout.id);
    }
    this.renderStructure(node, layout);
    return node;
  }

  /** Build the inner DOM only when the shape/size actually changed. */
  private renderStructure(node: HTMLElement, layout: HudElementLayout): void {
    const spec = layout.custom;
    if (!spec) return;
    const sig = JSON.stringify(spec);
    if (this.customSig.get(node.id) === sig) return;
    this.customSig.set(node.id, sig);
    this.spark.delete(node.id);
    node.replaceChildren();
    node.className = `hud-custom hud-custom-${spec.kind}`;
    node.style.pointerEvents = "none";
    if (spec.kind === "ring") {
      const size = spec.radius * 2;
      node.style.width = `${size}px`;
      node.style.height = `${size}px`;
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", String(size));
      svg.setAttribute("height", String(size));
      svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
      const track = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      const fill = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      for (const c of [track, fill]) {
        c.setAttribute("cx", String(spec.radius));
        c.setAttribute("cy", String(spec.radius));
        c.setAttribute("r", String(Math.max(1, spec.radius - spec.thickness / 2)));
        c.setAttribute("fill", "none");
        c.setAttribute("stroke-width", String(spec.thickness));
        c.setAttribute("stroke-linecap", "round");
        c.setAttribute("transform", `rotate(-90 ${spec.radius} ${spec.radius})`);
      }
      track.setAttribute("stroke", "rgba(255,255,255,0.16)");
      fill.setAttribute("stroke", "#59e06b");
      svg.append(track, fill);
      node.append(svg);
    } else if (spec.kind === "bar") {
      node.style.width = `${spec.width}px`;
      node.style.height = `${spec.height}px`;
      const track = el("div", "hud-bar-track");
      const fill = el("div", "hud-bar-fill");
      track.append(fill);
      node.append(track);
    } else if (spec.kind === "pips") {
      node.style.width = `${spec.width}px`;
      node.style.height = `${spec.height}px`;
      const row = el("div", "hud-pips");
      for (let i = 0; i < spec.segments; i++) row.append(el("span", "hud-pip"));
      node.append(row);
    } else {
      node.style.width = `${spec.width}px`;
      node.style.height = `${spec.height}px`;
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", String(spec.width));
      svg.setAttribute("height", String(spec.height));
      svg.setAttribute("viewBox", `0 0 ${spec.width} ${spec.height}`);
      const line = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
      line.setAttribute("fill", "none");
      line.setAttribute("stroke-width", "1.5");
      line.setAttribute("stroke-linejoin", "round");
      line.setAttribute("stroke-linecap", "round");
      svg.append(line);
      node.append(svg);
    }
    if (spec.showValue) node.append(el("div", "hud-custom-value"));
    if (spec.label) node.append(el("div", "hud-custom-label"));
  }

  private apply(): void {
    // A malformed HUD document must never abort menu construction or map load.
    try {
      this.applyInner();
    } catch (err) {
      console.error("HUD apply failed", err);
    }
  }

  private applyInner(): void {
    this.syncCustomNodes();
    const entries = this.entries();
    const stage = hudStageSize();
    const vw = stage.w;
    const vh = stage.h;

    const shown = new Set<string>();
    for (const e of entries) {
      const visible = this.isVisible(e.id);
      e.el.classList.toggle("hud-el-hidden", !visible);
      shown.add(e.id);
      if (!visible) continue;
      e.el.style.translate = "";
      e.el.style.scale = "";
    }
    // Group containers (`#combat-hud`, …) are not layout entries; apply their
    // game-state visibility here so there is still exactly one visibility owner.
    for (const id of this.gameShown.keys()) {
      if (shown.has(id)) continue;
      document.getElementById(id)?.classList.toggle("hud-el-hidden", !this.isVisible(id));
    }

    const measured = new Map<string, DOMRect>();
    for (const e of entries) {
      if (!this.isVisible(e.id)) continue;
      const r = e.el.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) measured.set(e.id, r);
    }

    for (const e of entries) {
      const layout = e.layout;
      if (!layout) continue;
      if (!this.isVisible(e.id)) continue;
      const rect = measured.get(e.id);
      if (!rect) continue;
      let dx = 0;
      let dy = 0;
      if (layout.anchor === "auto") {
        // Keep the shipped CSS position; the offset is a plain nudge composed
        // with the element's own `transform` (same behaviour the old movement
        // aid offsets had).
        dx = layout.offset.x;
        dy = layout.offset.y;
      } else {
        const { h, v } = parts(layout.anchor);
        if (h !== null && v !== null) {
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const s = layout.scale;
          const px = cx + (pivotX(rect, h) - cx) * s;
          const py = cy + (pivotY(rect, v) - cy) * s;
          const tx = h === "left" ? layout.offset.x : h === "right" ? vw - layout.offset.x : vw / 2 + layout.offset.x;
          const ty = v === "top" ? layout.offset.y : v === "bottom" ? vh - layout.offset.y : vh / 2 + layout.offset.y;
          dx = Math.round((tx - px) * 100) / 100;
          dy = Math.round((ty - py) * 100) / 100;
        }
        // Keep anchored elements fully on stage. The editor is meant for
        // positioning, not for losing an element off the edge; this also
        // rescues layouts saved before this clamp existed.
        const margin = 4;
        const left = rect.left + dx;
        const top = rect.top + dy;
        if (left < margin) dx += margin - left;
        else if (left + rect.width > vw - margin) dx -= left + rect.width - (vw - margin);
        if (top < margin) dy += margin - top;
        else if (top + rect.height > vh - margin) dy -= top + rect.height - (vh - margin);
      }
      e.el.style.translate = dx || dy ? `${dx}px ${dy}px` : "";
      e.el.style.scale = layout.scale !== 1 ? String(layout.scale) : "";
      // The cgaz strip owns its own alpha (`cgazStyle.opacity`): it hides
      // itself dynamically when there is no acceleration profile. Clearing
      // the inline opacity here on the periodic reconcile would make the
      // strip vanish after ~750 ms even while the profile is present.
      if (e.id !== "cgaz-hud") {
        e.el.style.opacity = layout.opacity !== 1 ? String(layout.opacity) : "";
      }
      e.el.style.zIndex = layout.z != null ? String(layout.z) : "";
      this.applyLook(e);
    }

    this.lastW = vw;
    this.lastH = vh;
    this.lastDpr = window.devicePixelRatio || 1;
    this.dirty = false;
    this.applyColors();
    this.applyValues(performance.now());
  }

  /** Borders, background, padding, text colour and bar tracks. */
  private applyLook(e: Entry): void {
    const look = e.layout?.look;
    if (!look) return;
    const node = e.el;
    const bg = look.background ? rgba(look.background, look.backgroundOpacity) : "";
    const border = look.borderColor && look.borderWidth > 0
      ? `${look.borderWidth}px solid ${look.borderColor}`
      : "";
    node.style.background = bg;
    node.style.border = border;
    if (bg || border || look.padding > 0) {
      node.style.borderRadius = look.borderRadius ? `${look.borderRadius}px` : "";
      node.style.padding = look.padding ? `${look.padding}px` : "";
      node.style.boxSizing = "border-box";
    } else if (!e.custom) {
      node.style.borderRadius = "";
      node.style.padding = "";
      node.style.boxSizing = "";
    }
    if (!e.custom) {
      if (look.textColor) node.style.color = look.textColor;
      return;
    }
    const track = rgba(look.trackColor ?? "#ffffff", look.trackOpacity);
    if (e.custom.kind === "bar") {
      const trackEl = node.querySelector<HTMLElement>(".hud-bar-track");
      const fill = node.querySelector<HTMLElement>(".hud-bar-fill");
      if (trackEl) {
        trackEl.style.background = track;
        trackEl.style.borderRadius = `${look.barRadius}px`;
      }
      if (fill) fill.style.opacity = String(look.fillOpacity);
    } else if (e.custom.kind === "ring") {
      const trackEl = node.querySelector<SVGCircleElement>("circle:first-of-type");
      const fill = node.querySelector<SVGCircleElement>("circle:last-of-type");
      if (trackEl) trackEl.setAttribute("stroke", track);
      if (fill) fill.setAttribute("stroke-opacity", String(look.fillOpacity));
    } else if (e.custom.kind === "spark") {
      const line = node.querySelector<SVGPolylineElement>("polyline");
      if (line) line.setAttribute("stroke-opacity", String(look.fillOpacity));
    }
  }

  private applyColors(): void {
    for (const e of this.entries()) {
      if (e.custom) continue;
      const paint = e.layout?.color;
      if (!paint?.enabled || !e.source) {
        if (e.layout?.look.textColor) e.el.style.color = e.layout.look.textColor;
        else e.el.style.removeProperty("color");
        continue;
      }
      const res = evaluatePaint(paint, this.valueFor(e.source));
      if (res) e.el.style.color = res.color;
    }
  }

  private applyValues(now: number): void {
    const specOf = new Map(
      this.doc.elements.filter((e) => e.custom).map((e) => [e.id, e] as const),
    );
    for (const [id, layout] of specOf) {
      const node = this.customNodes.get(id);
      const spec = layout.custom;
      if (!node || !spec) continue;
      const raw = this.valueFor(spec.source);
      const v = clamp(raw, spec.min, spec.max);
      const span = spec.max - spec.min;
      const frac = span > 0 ? clamp((v - spec.min) / span, 0, 1) : 0;
      const color = (layout.color?.enabled ? evaluatePaint(layout.color, raw)?.color : null) ?? "#59e06b";
      const text = `${Math.round(v)}${spec.suffix}`;
      const valueEl = node.querySelector<HTMLElement>(".hud-custom-value");
      const labelEl = node.querySelector<HTMLElement>(".hud-custom-label");
      if (valueEl) {
        valueEl.textContent = text;
        valueEl.style.fontSize = `${spec.valueSize}px`;
        // White (or the chosen text colour) stays readable over the band fill.
        valueEl.style.color = layout.look?.textColor ?? "#ffffff";
      }
      if (labelEl) {
        labelEl.textContent = spec.label;
        labelEl.style.fontSize = `${spec.labelSize}px`;
      }
      if (spec.kind === "bar") {
        const fill = node.querySelector<HTMLElement>(".hud-bar-fill");
        if (fill) {
          fill.style.background = color;
          const horizontal = spec.direction === "right" || spec.direction === "left";
          fill.style.transformOrigin =
            spec.direction === "left" ? "right center" : spec.direction === "up" ? "center bottom" : spec.direction === "down" ? "center top" : "left center";
          fill.style.transform = horizontal ? `scaleX(${frac})` : `scaleY(${frac})`;
        }
      } else if (spec.kind === "ring") {
        const fill = node.querySelector<SVGCircleElement>("circle:last-of-type");
        if (fill) {
          const r = Math.max(1, spec.radius - spec.thickness / 2);
          const circ = 2 * Math.PI * r;
          fill.setAttribute("stroke-dasharray", String(circ));
          fill.setAttribute("stroke-dashoffset", String(circ * (1 - frac)));
          fill.setAttribute("stroke", color);
        }
      } else if (spec.kind === "pips") {
        const cells = node.querySelectorAll<HTMLElement>(".hud-pip");
        const filled = Math.round(frac * spec.segments);
        const track = rgba(layout.look?.trackColor ?? "#ffffff", layout.look?.trackOpacity ?? 0.16);
        const fillOpacity = layout.look?.fillOpacity ?? 1;
        cells.forEach((c, i) => {
          if (i < filled) {
            c.style.background = color;
            c.style.opacity = String(fillOpacity);
          } else {
            c.style.background = track;
            c.style.opacity = "1";
          }
        });
      } else {
        let h = this.spark.get(id);
        if (!h) {
          h = { values: new Array(32).fill(v), last: 0 };
          this.spark.set(id, h);
        }
        if (now - h.last >= spec.sampleMs) {
          h.last = now;
          h.values.push(v);
          while (h.values.length > 64) h.values.shift();
        }
        const line = node.querySelector<SVGPolylineElement>("polyline");
        if (line) {
          const w = spec.width;
          const hh = spec.height;
          const n = Math.max(2, h.values.length);
          const pts = h.values.map((val, i) => {
            const f = span > 0 ? clamp((val - spec.min) / span, 0, 1) : 0;
            return `${((i / (n - 1)) * w).toFixed(1)},${(hh - f * (hh - 2) - 1).toFixed(1)}`;
          });
          line.setAttribute("points", pts.join(" "));
          line.setAttribute("stroke", color);
        }
      }
    }
  }
}
